import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { format } from 'date-fns';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { CompanyService } from 'src/app/root/company.service';

@Component({
  selector: 'lohi-shifts-per-day-chart',
  templateUrl: './shifts-per-day-chart.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftsPerDayChartComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public dateFormat = 'M/dd/yyyy';
  public labels: Array<string>;

  public chart: Chart;

  constructor(private companyService: CompanyService, private cd: ChangeDetectorRef) {}

  public ngOnInit() {
    this.companyService.shiftPerDay$.pipe(takeUntil(this.destroy$$)).subscribe(async (data) => {
      if (!data || !data?.entries.length) {
        return;
      }
      const labels = data.entries.map((entry) =>
        dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString()),
      );
      const metrics = await this.companyService.companyMetrics30Day$
        .pipe(
          filter((m) => !!m),
          take(1),
        )
        .toPromise();
      this.chart = new Chart({
        chart: {
          type: 'column',
        },
        title: {
          align: 'left',
          useHTML: true,
          text: `<div class="text-lg font-bold">Shifts Per Day <span class="text-blue-500">${
            metrics.daysOfHistory || 30
          }d</span></div>`,
        },
        credits: {
          enabled: false,
        },
        yAxis: [
          {
            labels: {
              enabled: false,
            },
            tickInterval: 0,
            title: { text: null },
          },
          {
            title: { text: 'Shifts' },
          },
        ],
        plotOptions: {
          series: {},
        },
        xAxis: {
          type: 'category',
          categories: labels,
          visible: true,
        },
        series: [],
        tooltip: {
          formatter() {
            return (
              `<span class="font-bold" style="color: ${this.point.color}">` + this.series.name + '</span> ' + this.y
            );
          },
        },
      });
      const totalShifts: Array<[string, number]> = [];
      for (const entry of data.entries) {
        const dateStr = dateFormat(new Date(entry.year, entry.month - 1, entry.day, 0, 0, 0, 0).toISOString());
        totalShifts.push([dateStr, entry.shiftCount]);
      }
      this.chart.addSeries(
        {
          name: 'Shifts',
          type: 'column',
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, '#070707'],
              [1, '#3e3e52'],
            ],
          },
          data: totalShifts,
          yAxis: 1,
        },
        true,
        true,
      );
      this.cd.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }
}

function dateFormat(date: string): string {
  return format(new Date(date), 'M/dd/yyyy', {
    useAdditionalDayOfYearTokens: true,
    useAdditionalWeekYearTokens: true,
  });
}
